import React, { useEffect } from 'react';

import ImageProxy from '@next-image-proxy';

import useSelfDataCheckLocalStorage from '@shared/hooks/useSelfDataCheckLocalStorage';

import CustomStoryBanner from '../CustomStoryBanner';
import CustomStoryLayout from '../CustomStoryLayout';
import CustomStorySections from '../CustomStorySections';
import CustomStoryStatCard from '../CustomStoryStatCard';
import { kols, partners, stats } from './data';
import StepsToJoinStormTradeCampaign from './StepsToJoinStormTradeCampaign';
import StormTradeStoryLeaderboard from './StormTradeStoryLeaderboard';

const sections = [
  {
    id: 'overview',
    title: 'Project Overview',
    content: (
      <>
        <p className="mb-5 md:mb-10 md:text-2xl font-light">
          The rapidly growing TON Ecosystem is welcoming its latest innovation, Storm Trade, the
          first perpetual DEX on the TON blockchain, backed by Ton Ventures. This groundbreaking
          platform integrates directly with Telegram to deliver a trading experience with up to 50x
          leverage and features a diverse array of trading options in crypto, commodities, stocks,
          and forex. Storm Trade is set to captivate the next 10 million DeFi users with its
          high-yield liquidity pools, social-fi elements like trading tournaments and NFT
          collections, and a user-friendly interface complete with TradingView charts. Ensuring
          top-tier security through full decentralization and advanced protocols, Storm Trade is
          poised to redefine the boundaries of decentralized trading.
        </p>

        <ImageProxy
          alt="First social-Fi leveraged in Telegram"
          className="w-full h-auto mb-4 rounded-2xl"
          width={2324}
          height={1066}
          src="/launchpad/stories/st/storm-trade-overview-1.png"
        />

        <ImageProxy
          alt="Trade with leverage up to x50"
          className="w-full h-auto rounded-2xl"
          src="/launchpad/stories/st/storm-trade-overview-2.png"
          width={2324}
          height={1067}
        />
      </>
    ),
  },
  {
    id: 'partners',
    title: 'Storm Trade Partners',
    content: (
      <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {partners.map(({ image, name }, i) => {
          return (
            <li
              className="flex items-center rounded-lg bg-primary-100 w-full p-[1.125rem] justify-center h-20"
              key={i}
            >
              <ImageProxy
                className="w-full h-full max-w-[15.375rem] object-contain"
                alt={name}
                quality={80}
                {...image}
              />
            </li>
          );
        })}
      </ul>
    ),
  },
  {
    id: 'kols',
    title: 'Storm Trade KOLs',
    content: (
      <ul className="flex flex-wrap gap-8">
        {kols.map((kol) => {
          return (
            <li key={kol.id} className="flex justify-center max-md:flex-grow">
              <span className="flex flex-col gap-2.5 w-[8.75rem] text-center font-semibold text-xl">
                <ImageProxy
                  alt={kol.name}
                  quality={80}
                  className="w-full rounded-full"
                  {...kol.image}
                />

                {kol.name}
              </span>
            </li>
          );
        })}
      </ul>
    ),
  },
  {
    id: 'campaign',
    title: 'About the Campaign',
    content: (
      <>
        <p className="mb-5 md:mb-10 md:text-2xl font-light">
          To celebrate the launch of the Magic Launchpad and the upcoming release of the $STORM
          Token by Storm Trade, they have allocated 5,000,000 $STORM tokens to the Storm Trade
          Road-to-IDO Campaign. This campaign aims to expand the Storm Trade ecosystem and
          community, rewarding the most active users on the platform and across social media.
          <br />
          Alongside the Campaign Prize Pool, participants in this campaign will also receive
          whitelisting for further allocations in the Storm Trade IDO, which will be hosted on the
          Magic Launchpad. More details will be provided soon.
          <br />
          Your level of activity and engagement directly increases your potential to earn more
          $STORM tokens!
          <br />
          Magic Square is proud to host and manage this campaign, offering complete support to all
          participants.
        </p>

        <ul className="flex flex-wrap gap-8 md:gap-[3.25rem]">
          {stats.map(({ description, name, stat, Icon }, i) => {
            return (
              <li key={i} className="flex-grow">
                <CustomStoryStatCard
                  title={name}
                  description={description}
                  stat={stat}
                  icon={<Icon />}
                />
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'join',
    title: 'Steps to Join the Campaign',
    content: <StepsToJoinStormTradeCampaign />,
  },
  {
    title: 'Leaderboard',
    id: 'leaderboard',
    content: <StormTradeStoryLeaderboard className="max-h-80" />,
  },
];

const StormTradeStory = () => {
  const { resetValue: resetSelfDataCheckLocalStorage } = useSelfDataCheckLocalStorage();

  useEffect(() => {
    // NOTE: reset local storage to initial state when page is opened
    // to make it consistent across tabs
    resetSelfDataCheckLocalStorage();
  }, [resetSelfDataCheckLocalStorage]);

  return (
    <CustomStoryLayout
      title="Storm Trade Project Overview and Campaign Details"
      banner={
        <CustomStoryBanner
          alt="Magic Launchpad Presents Storm Trade Road-to-IDO 5,000,000 $STORM Prize Pool"
          desktop={{
            src: '/launchpad/stories/st/storm-trade-banner-desktop.png',
            width: 1300,
            height: 225,
            quality: 80,
          }}
          tablet={{
            src: '/launchpad/stories/st/storm-trade-banner-tablet.png',
            width: 1300,
            height: 570,
            quality: 80,
          }}
          mobile={{
            src: '/launchpad/stories/st/storm-trade-banner-mobile.png',
            width: 1500,
            height: 570,
            quality: 80,
          }}
        />
      }
    >
      <CustomStorySections sections={sections} />
    </CustomStoryLayout>
  );
};

export default StormTradeStory;
